import { useState } from "react";
import { FadeUp } from "../components/Animations";
import { Send, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Residential Design",
  "Kitchen Remodel",
  "Bathroom Remodel",
  "Full Home Remodel",
  "New Build",
  "Commercial",
  "Other",
];

const BUDGET_RANGES = [
  "Under $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "$250,000+",
  "Not Sure Yet",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    budget_range: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-page" className="pt-20 min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <FadeUp>
            <CheckCircle size={48} className="text-taupe mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-5xl font-light text-onyx mb-4">
              Thank you.
            </h2>
            <p className="text-sm font-light text-onyx/60 max-w-md mx-auto">
              Your inquiry has been received. We'll be in touch within 48 hours
              to discuss your project.
            </p>
          </FadeUp>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 px-6 md:px-16 max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
            Contact
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-onyx leading-[1.05] max-w-3xl">
            Let's create something intentional.
          </h1>
        </FadeUp>
      </section>

      {/* Form + Info */}
      <section className="px-6 md:px-16 max-w-[1400px] mx-auto pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
          {/* Form */}
          <div className="md:col-span-7">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <FadeUp>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
                  <div className="mb-8">
                    <label className="text-xs tracking-[0.2em] uppercase text-taupe block mb-2">
                      Name *
                    </label>
                    <input
                      data-testid="contact-name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="editorial-input"
                      required
                    />
                  </div>
                  <div className="mb-8">
                    <label className="text-xs tracking-[0.2em] uppercase text-taupe block mb-2">
                      Email *
                    </label>
                    <input
                      data-testid="contact-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="editorial-input"
                      required
                    />
                  </div>
                  <div className="mb-8">
                    <label className="text-xs tracking-[0.2em] uppercase text-taupe block mb-2">
                      Phone
                    </label>
                    <input
                      data-testid="contact-phone"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(480) 000-0000"
                      className="editorial-input"
                    />
                  </div>
                  <div className="mb-8">
                    <label className="text-xs tracking-[0.2em] uppercase text-taupe block mb-2">
                      Project Type
                    </label>
                    <select
                      data-testid="contact-project-type"
                      name="project_type"
                      value={form.project_type}
                      onChange={handleChange}
                      className="editorial-input appearance-none cursor-pointer"
                    >
                      <option value="">Select type</option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </FadeUp>

              <FadeUp delay={0.15}>
                <div className="mb-8">
                  <label className="text-xs tracking-[0.2em] uppercase text-taupe block mb-2">
                    Budget Range
                  </label>
                  <select
                    data-testid="contact-budget"
                    name="budget_range"
                    value={form.budget_range}
                    onChange={handleChange}
                    className="editorial-input appearance-none cursor-pointer"
                  >
                    <option value="">Select range</option>
                    {BUDGET_RANGES.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              </FadeUp>

              <FadeUp delay={0.25}>
                <div className="mb-10">
                  <label className="text-xs tracking-[0.2em] uppercase text-taupe block mb-2">
                    Message *
                  </label>
                  <textarea
                    data-testid="contact-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project..."
                    rows={4}
                    className="editorial-input resize-none"
                    required
                  />
                </div>
              </FadeUp>

              {error && (
                <p data-testid="contact-error" className="text-red-600 text-sm mb-4">
                  {error}
                </p>
              )}

              <FadeUp delay={0.35}>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 bg-onyx text-bone px-10 py-4 text-sm tracking-[0.2em] uppercase font-light hover:bg-taupe transition-colors duration-500 disabled:opacity-50"
                >
                  {sending ? "Sending..." : "Send Inquiry"}
                  <Send size={16} />
                </button>
              </FadeUp>
            </form>
          </div>

          {/* Info Sidebar */}
          <div className="md:col-span-5 md:pt-2">
            <FadeUp delay={0.2}>
              <div className="border-t border-concrete pt-8 mb-10">
                <p className="text-xs tracking-[0.25em] uppercase text-taupe mb-4">
                  Location
                </p>
                <p className="text-base font-light text-onyx">
                  Scottsdale, Arizona
                </p>
                <p className="text-sm font-light text-onyx/60 mt-1">
                  Serving Phoenix, Paradise Valley, Scottsdale &amp; surrounding areas
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <div className="border-t border-concrete pt-8 mb-10">
                <p className="text-xs tracking-[0.25em] uppercase text-taupe mb-4">
                  Email
                </p>
                <a
                  href="mailto:jamie@jdesignhaus.com"
                  className="text-base font-light text-onyx hover:text-taupe transition-colors"
                >
                  jamie@jdesignhaus.com
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.4}>
              <div className="border-t border-concrete pt-8 mb-10">
                <p className="text-xs tracking-[0.25em] uppercase text-taupe mb-4">
                  Phone
                </p>
                <a
                  href="tel:480-527-8858"
                  className="text-base font-light text-onyx hover:text-taupe transition-colors"
                >
                  480-527-8858
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.5}>
              <div className="border-t border-concrete pt-8">
                <p className="text-xs tracking-[0.25em] uppercase text-taupe mb-4">
                  Social
                </p>
                <a
                  href="https://www.instagram.com/jdesignhaus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base font-light text-onyx hover:text-taupe transition-colors"
                >
                  @jdesignhaus
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
